import { useState } from 'react';
import { cabinet } from '../../api/cabinet';
import { isApiError } from '../../api/client';
import { Alert } from '../../components/Alert';
import { useT } from '../../i18n';
import { useAsync } from '../../lib/useAsync';
import { FeedbackForm } from './FeedbackForm';

/** A request was sent (or written to the cabinet) and is not answered yet. */
export const OPEN_FEEDBACK = new Set(['sent', 'logged']);

/**
 * Small «Відгук» card on the case page, only when GET /cabinet/feedback has an open request for this case.
 * Nothing at all otherwise (also on 404 while the backend has no feedback yet).
 */
export function CaseFeedbackCard({ caseId }: { caseId: string }) {
  const t = useT();
  const [done, setDone] = useState(false);
  const { data } = useAsync(async () => {
    try {
      return (await cabinet.feedback()).items;
    } catch {
      return [];
    }
  }, [caseId]);
  const open = (data ?? []).find(
    (i) => String(i.case_id) === caseId && (i.can_answer ?? (OPEN_FEEDBACK.has(i.status) && !i.answered_at)),
  );

  if (done)
    return (
      <section aria-labelledby="fb-h" className="border border-line p-5 sm:p-6">
        <h2 id="fb-h" className="text-[15px]">
          {t('feedback.card')}
        </h2>
        <div className="mt-3">
          <Alert kind="success">{t('feedback.thanks')}</Alert>
        </div>
      </section>
    );
  if (!open) return null;

  return (
    <section aria-labelledby="fb-h" id="feedback" className="scroll-mt-6 border border-line p-5 sm:p-6">
      <h2 id="fb-h" className="text-[18px]">
        {t('feedback.card')}
      </h2>
      <p className="mt-2 text-[15px] leading-relaxed text-txt">{t('feedback.cardIntro')}</p>
      <FeedbackForm
        onSubmit={async (v) => {
          try {
            await cabinet.answerFeedback(caseId, { rating: v.rating, ...(v.text ? { text: v.text } : {}) });
          } catch (e) {
            // already answered elsewhere (e.g. from the e-mail link) — nothing more to ask
            if (!(isApiError(e) && e.status === 409)) throw e;
          }
          setDone(true);
        }}
      />
    </section>
  );
}
